import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowRight, MailWarning, RefreshCcw, CheckCircle2, ArrowLeft, AlertTriangle, ShieldOff, KeyRound } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { api, apiError } from "@/lib/api";
import { resolveTurnstileSiteKey } from "@/lib/turnstile";
import { DEMO_ACTIVO, SESION_DEMO } from "@/demo/modo";
import { toast } from "sonner";

import { HERO_LOGIN as HERO } from "@/lib/imagenes";
// Cloudflare Turnstile (jul-2026). Ver `lib/turnstile.js` para la lógica de fallback preview.
const TURNSTILE_SITE_KEY = resolveTurnstileSiteKey();

export default function Login() {
  const { login, loginMfaVerify, loginMfaVerifyBackup, loginMfaResend } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // MODO DEMO: credenciales de demostración precargadas (en producción quedan vacías).
  const [email, setEmail] = useState(SESION_DEMO?.credenciales?.email || "");
  const [password, setPassword] = useState(SESION_DEMO?.credenciales?.password || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Gate de verificación de correo
  const [emailNoVerificado, setEmailNoVerificado] = useState(null); // {email}
  const [reenviando, setReenviando] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  // ago-2026 · resultado REAL del reenvío ({codigo, mensaje}). Antes solo había un booleano
  // que se ponía en true con cualquier 200, incluso cuando el backend no enviaba nada.
  const [reenvioResultado, setReenvioResultado] = useState(null);
  // Turnstile — token del captcha. Se resetea al cambiar de intento (widget muestra
  // nuevo challenge cuando expira o falla) para no reusar tokens (Cloudflare los rechaza).
  const [cfToken, setCfToken] = useState("");
  const turnstileRef = useRef(null);
  // 2FA Fase 5: sub-vista del reto MFA. `mfaState` en memoria (React state) — al recargar
  // la página el challenge muere y el usuario vuelve al form de correo+contraseña.
  const [mfaState, setMfaState] = useState(null); // { challenge, email_masked, mfa_send_failed }
  const [mfaCodigo, setMfaCodigo] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaResending, setMfaResending] = useState(false);
  const [mfaResendCooldown, setMfaResendCooldown] = useState(0); // segundos restantes
  const mfaInputRef = useRef(null);
  const mfaAutoSubmitRef = useRef(null); // timer del auto-envío (Fase 6 post-QA)
  // 2FA Bloque 2 (ago-2026): sub-vista de CÓDIGO DE RESPALDO. Solo se ofrece cuando el
  // backend indica `backup_disponible` (hoy: exclusivo del super_admin con juego vigente).
  const [modoBackup, setModoBackup] = useState(false);
  const [backupCodigo, setBackupCodigo] = useState("");
  const [backupVerifying, setBackupVerifying] = useState(false);
  // Retraso del auto-envío tras completar 6 dígitos. 300 ms fue muy corto para corregir
  // — se ajustó a 500 ms tras pruebas manuales (feb-2026).
  const MFA_AUTOSUBMIT_MS = 500;
  // Aviso 2FA apagada por entidad (jul 2026): pinta banner cuando super_admin apagó
  // `dosfa_obligatorio` para la entidad del correo tecleado. Consulta pública, sin auth.
  const [dosfaEntidad, setDosfaEntidad] = useState(null); // {entidad} o null si obligatorio
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!e || !e.includes("@")) { setDosfaEntidad(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/dosfa-status", { params: { email: e } });
        if (data && data.dosfa_obligatorio === false) {
          setDosfaEntidad({ entidad: data.entidad || "" });
        } else {
          setDosfaEntidad(null);
        }
      } catch { setDosfaEntidad(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [email]);

  // Al entrar a la vista del código damos foco al input y arrancamos la espera de 60 s,
  // porque al iniciar sesión ya salió un código. Si Brevo NO lo aceptó
  // (`mfa_send_failed`), la espera no arranca: el usuario puede pedir otro de inmediato.
  useEffect(() => {
    if (mfaState) {
      setTimeout(() => { try { mfaInputRef.current?.focus(); } catch (_e) { /* no-op */ } }, 50);
      if (!mfaState.mfa_send_failed) setMfaResendCooldown((s) => (s > 0 ? s : 60));
    }
  }, [mfaState]);

  // Contador descendente del cooldown de reenvío
  useEffect(() => {
    if (mfaResendCooldown <= 0) return;
    const t = setTimeout(() => setMfaResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [mfaResendCooldown]);

  useEffect(() => {
    // Post-verificación: /login?verificado=1 muestra un toast de éxito.
    const params = new URLSearchParams(location.search);
    if (params.get("verificado") === "1") {
      toast.success("Correo verificado. Ya puedes iniciar sesión.");
    }
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailNoVerificado(null);
    setReenviado(false);
    if (!cfToken) {
      setError("Por favor completa la verificación de seguridad antes de iniciar sesión.");
      return;
    }
    setLoading(true);
    const res = await login(email, password, cfToken);
    setLoading(false);
    // Turnstile rechaza tokens repetidos — resetear el widget siempre después de un submit,
    // sea exitoso o no, para que el próximo intento tenga un token limpio.
    try { turnstileRef.current?.reset(); } catch (_e) { /* no-op */ }
    setCfToken("");
    if (res.ok) { navigate("/dashboard"); return; }
    // 2FA Fase 5: reto MFA. Cambio a la sub-vista del código.
    if (res.mfa) {
      setMfaState({
        challenge: res.challenge,
        email_masked: res.email_masked || "",
        mfa_send_failed: !!res.mfa_send_failed,
        backup_disponible: !!res.backup_disponible,
        backup_restantes: res.backup_restantes ?? 0,
        backup_aviso_bajo: !!res.backup_aviso_bajo,
      });
      setMfaCodigo("");
      setMfaError("");
      setModoBackup(false);
      setBackupCodigo("");
      return;
    }
    if (res.codigo === "email_no_verificado") {
      setEmailNoVerificado({ email: res.email || email });
      return;
    }
    setError(res.error);
  };

  // 2FA Fase 5: submit del código de 6 dígitos. Acepta un `codigoArg` para poder usarlo
  // desde el auto-submit (evita depender del state que puede no haberse actualizado aún).
  const submitMfa = async (ev, codigoArg) => {
    ev?.preventDefault?.();
    // Cancela cualquier auto-submit pendiente para que no dispare dos verificaciones seguidas.
    if (mfaAutoSubmitRef.current) {
      clearTimeout(mfaAutoSubmitRef.current);
      mfaAutoSubmitRef.current = null;
    }
    setMfaError("");
    if (!mfaState?.challenge) return;
    const raw = codigoArg !== undefined ? codigoArg : mfaCodigo;
    const codigo = (raw || "").replace(/\D/g, "").slice(0, 6);
    if (codigo.length !== 6) {
      setMfaError("El código tiene 6 dígitos.");
      return;
    }
    setMfaVerifying(true);
    const res = await loginMfaVerify(mfaState.challenge, codigo);
    setMfaVerifying(false);
    if (res.ok) { navigate("/dashboard"); return; }
    if (res.expirado) {
      // El challenge o el código expiraron: volver al paso 1 para reintentar login.
      setMfaState(null);
      setMfaCodigo("");
      setError("Tu sesión de verificación expiró. Vuelve a iniciar sesión.");
      return;
    }
    if (res.limite) {
      setMfaError("Demasiados intentos. Espera unos minutos e intenta de nuevo. Si sigues sin poder entrar, contacta al administrador de la plataforma.");
      return;
    }
    setMfaError("Código incorrecto. Verifica e intenta de nuevo.");
  };

  // 2FA Bloque 2 (ago-2026): canje con código de respaldo (puerta de emergencia).
  const submitBackup = async (ev) => {
    ev?.preventDefault?.();
    setMfaError("");
    if (!mfaState?.challenge) return;
    const codigo = (backupCodigo || "").trim();
    if (codigo.replace(/[^A-Za-z0-9]/g, "").length < 15) {
      setMfaError("El código de respaldo tiene 15 caracteres (3 grupos de 5).");
      return;
    }
    setBackupVerifying(true);
    const res = await loginMfaVerifyBackup(mfaState.challenge, codigo);
    setBackupVerifying(false);
    if (res.ok) {
      if (res.backup_aviso_bajo) {
        toast.warning(`Entraste con un código de respaldo. Te quedan ${res.backup_restantes}: genera un juego nuevo desde el panel.`,
          { duration: 10000 });
      } else {
        toast.success(`Acceso con código de respaldo. Códigos restantes: ${res.backup_restantes}.`);
      }
      navigate("/dashboard");
      return;
    }
    if (res.expirado) {
      setMfaState(null);
      setModoBackup(false);
      setBackupCodigo("");
      setError("Tu sesión de verificación expiró. Vuelve a iniciar sesión.");
      return;
    }
    if (res.limite) {
      setMfaError("Demasiados intentos. Espera unos minutos e intenta de nuevo.");
      return;
    }
    setMfaError("Ese código de respaldo no es válido o ya se usó. Cada código sirve una sola vez.");
  };

  // 2FA Fase 5 · oct-2026: reenvío del código. Espera de 60 s entre envíos (la aplica
  // también el backend) y tope de 5 envíos cada 15 minutos por usuario. Si Brevo sigue
  // fallando (mfa_send_failed:true), NO se activa la espera para que el usuario pueda
  // reintentar hasta que salga el correo. Los códigos anteriores SIGUEN SIRVIENDO.
  const resendMfa = async () => {
    if (!mfaState?.challenge) return;
    if (mfaResendCooldown > 0 && !mfaState.mfa_send_failed) return;
    setMfaResending(true);
    setMfaError("");
    const res = await loginMfaResend(mfaState.challenge);
    setMfaResending(false);
    if (!res.ok) {
      setMfaError(res.limite
        ? "Alcanzaste el máximo de 5 códigos en 15 minutos. Espera 15 minutos o usa el último código que te llegó: los códigos anteriores siguen siendo válidos."
        : "No pudimos reenviar el código. Intenta de nuevo en un momento o contacta al administrador de la plataforma.");
      return;
    }
    // Refleja el estado real del envío para el usuario
    setMfaState((prev) => ({
      ...prev,
      mfa_send_failed: !!res.mfa_send_failed,
      backup_disponible: res.backup_disponible ?? prev.backup_disponible,
      backup_restantes: res.backup_restantes ?? prev.backup_restantes,
    }));
    if (res.codigo === "espera_60s") {
      setMfaResendCooldown(res.reintentar_en || 60);
      toast.info(res.mensaje || "Espera unos segundos antes de pedir otro código.");
      return;
    }
    // Espera solo si el envío fue exitoso (aparta la puerta de escape cuando Brevo falla)
    if (!res.mfa_send_failed) {
      setMfaResendCooldown(res.reintentar_en || 60);
      toast.success("Enviamos un código nuevo a tu correo. El anterior también sigue sirviendo.");
    } else {
      toast.warning("El código no se pudo enviar por correo. Intenta reenviar de nuevo o contacta al administrador.");
    }
  };

  // 2FA Fase 5: cancelar / volver a la pantalla de correo+contraseña.
  const cancelMfa = () => {
    if (mfaAutoSubmitRef.current) {
      clearTimeout(mfaAutoSubmitRef.current);
      mfaAutoSubmitRef.current = null;
    }
    setMfaState(null);
    setMfaCodigo("");
    setMfaError("");
    setMfaResendCooldown(0);
    setModoBackup(false);
    setBackupCodigo("");
  };

  const reenviarVerificacion = async () => {
    setReenviando(true);
    try {
      const { data } = await api.post("/auth/reenviar-verificacion",
        { email: emailNoVerificado?.email || email });
      const codigo = data?.codigo || "neutral";
      setReenvioResultado({ codigo, mensaje: data?.mensaje || "" });
      setReenviado(codigo === "enviado");
      if (codigo === "ya_verificado") {
        // El correo ya estaba verificado: quitamos el aviso para que vuelva a intentar entrar
        // en lugar de seguir pidiendo correos que nunca se van a enviar.
        setEmailNoVerificado(null);
        toast.success(data.mensaje);
      } else if (codigo === "fallo_envio") {
        toast.error(data.mensaje);
      } else if (data?.enviado) {
        toast.success(data.mensaje);
      } else {
        toast.info(data?.mensaje || "");
      }
    } catch (e) {
      setReenvioResultado({ codigo: "error", mensaje: apiError(e) });
      toast.error(apiError(e));
    } finally { setReenviando(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2 xl:px-20">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8">
            <VoraLogo variant="horizontal" tone="dark" />
            <p className="mt-2 text-xs text-slate-500">Sistema automatizado de cumplimiento · México</p>
          </div>

          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            {mfaState ? "Verifica tu identidad" : "Iniciar sesión"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {mfaState && modoBackup
              ? "Escribe uno de tus códigos de respaldo. Cada código sirve una sola vez."
              : mfaState
                ? "Enviamos un código de 6 dígitos a tu correo para confirmar el acceso."
                : "Accede a tu panel de cumplimiento y operación."}
          </p>

          {mfaState && modoBackup ? (
            <form onSubmit={submitBackup} className="mt-8 space-y-4" data-testid="mfa-backup-form">
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm">
                <div className="flex items-start gap-2">
                  <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Acceso con código de respaldo</p>
                    <p className="mt-0.5 text-xs text-amber-800">
                      Úsalo solo si el correo con el código no llega. Al usarlo queda registrado
                      en bitácora con fecha, hora e IP.
                      {mfaState.backup_restantes
                        ? <> Te quedan <b data-testid="mfa-backup-restantes">{mfaState.backup_restantes}</b> códigos.</>
                        : null}
                    </p>
                  </div>
                </div>
              </div>

              {mfaState.backup_aviso_bajo && (
                <div data-testid="mfa-backup-aviso-bajo"
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                  Quedan 3 códigos o menos. Genera un juego nuevo desde el panel de Super
                  Administrador en cuanto entres.
                </div>
              )}

              <div>
                <Label htmlFor="mfa-backup" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Código de respaldo
                </Label>
                <Input id="mfa-backup" data-testid="mfa-backup-input"
                  autoComplete="one-time-code" maxLength={20}
                  value={backupCodigo}
                  onChange={(e) => setBackupCodigo((e.target.value || "").toUpperCase())}
                  placeholder="XXXXX-XXXXX-XXXXX"
                  className="text-center font-mono text-lg tracking-[0.2em]" />
              </div>

              {mfaError && (
                <div data-testid="mfa-backup-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {mfaError}
                </div>
              )}

              <Button data-testid="mfa-backup-submit" type="submit"
                disabled={backupVerifying || !backupCodigo}
                className="w-full bg-primary hover:bg-sidebar-hover">
                {backupVerifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Entrar con código de respaldo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="flex items-center justify-between">
                <button type="button" data-testid="mfa-backup-cancel" onClick={cancelMfa}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-primary underline-offset-2 hover:underline">
                  <ArrowLeft className="h-3.5 w-3.5" /> Volver al inicio de sesión
                </button>
                <button type="button" data-testid="mfa-backup-volver-codigo"
                  onClick={() => { setModoBackup(false); setMfaError(""); }}
                  className="text-xs font-medium text-accent hover:underline">
                  Usar el código del correo
                </button>
              </div>
            </form>
          ) : mfaState ? (
            <form onSubmit={submitMfa} className="mt-8 space-y-4" data-testid="mfa-form">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="flex-1">
                    <p className="text-slate-700">Código enviado a</p>
                    <p data-testid="mfa-email-masked" className="font-mono text-slate-900">{mfaState.email_masked || "tu correo"}</p>
                    <p className="mt-1 text-[11px] text-slate-500">El código expira en 10 minutos.</p>
                  </div>
                </div>
              </div>

              {/* oct-2026 · Outlook/Hotmail retiene algunos correos 2-5 min. Se le dice al
                  usuario ANTES de que empiece a pedir códigos nuevos. */}
              <p data-testid="mfa-aviso-demora"
                className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-xs leading-relaxed text-slate-600">
                Te enviamos un código a tu correo. Puede tardar hasta 2 minutos. Si no lo ves,
                revisa tu carpeta de correo no deseado antes de solicitar otro.
              </p>

              {mfaState.mfa_send_failed && (
                <div data-testid="mfa-send-failed" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div>
                      <p className="font-medium">El correo no se pudo enviar en este momento.</p>
                      <p className="mt-0.5 text-xs text-amber-800">
                        Toca &quot;Reenviar código&quot; abajo. Si el problema persiste, contacta al administrador de la plataforma.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="mfa-codigo" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Código de verificación
                </Label>
                <Input id="mfa-codigo" data-testid="mfa-codigo-input"
                  ref={mfaInputRef}
                  inputMode="numeric" autoComplete="one-time-code"
                  pattern="[0-9]{6}" maxLength={6}
                  value={mfaCodigo}
                  onChange={(e) => {
                    const val = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                    setMfaCodigo(val);
                    // Auto-envío al completar 6 dígitos (Fase 6 post-QA, feb-2026):
                    // pequeño retraso para permitir corregir el último dígito antes de enviar.
                    if (mfaAutoSubmitRef.current) {
                      clearTimeout(mfaAutoSubmitRef.current);
                      mfaAutoSubmitRef.current = null;
                    }
                    if (val.length === 6 && !mfaVerifying) {
                      mfaAutoSubmitRef.current = setTimeout(() => {
                        mfaAutoSubmitRef.current = null;
                        submitMfa(null, val);
                      }, MFA_AUTOSUBMIT_MS);
                    }
                  }}
                  onPaste={(e) => {
                    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
                    if (txt) {
                      e.preventDefault();
                      setMfaCodigo(txt);
                      // Pegar código completo → auto-envío inmediato tras el retraso
                      if (mfaAutoSubmitRef.current) {
                        clearTimeout(mfaAutoSubmitRef.current);
                        mfaAutoSubmitRef.current = null;
                      }
                      if (txt.length === 6 && !mfaVerifying) {
                        mfaAutoSubmitRef.current = setTimeout(() => {
                          mfaAutoSubmitRef.current = null;
                          submitMfa(null, txt);
                        }, MFA_AUTOSUBMIT_MS);
                      }
                    }
                  }}
                  placeholder="000000"
                  className="text-center font-mono text-2xl tracking-[0.5em]" />
              </div>

              {mfaError && (
                <div data-testid="mfa-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {mfaError}
                </div>
              )}

              <div className="space-y-1">
                <Button data-testid="mfa-submit" type="submit"
                  disabled={mfaVerifying || mfaCodigo.length !== 6}
                  className="w-full bg-primary hover:bg-sidebar-hover">
                  {mfaVerifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Verificar y entrar <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                {mfaCodigo.length !== 6 && !mfaVerifying && (
                  <p data-testid="mfa-submit-hint" className="text-center text-[11px] text-slate-500">
                    Ingresa los 6 dígitos del código para continuar.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <button type="button" data-testid="mfa-cancel"
                  onClick={cancelMfa}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-primary underline-offset-2 hover:underline">
                  <ArrowLeft className="h-3.5 w-3.5" /> Volver al inicio de sesión
                </button>
                {/* Reenvío: si mfa_send_failed vino true, se habilita DE INMEDIATO (Fase 6
                    post-QA). La espera de 60 s solo aplica cuando el envío fue exitoso, y el
                    backend la aplica también. */}
                <button type="button" data-testid="mfa-resend"
                  onClick={resendMfa}
                  disabled={mfaResending || (mfaResendCooldown > 0 && !mfaState.mfa_send_failed)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline disabled:text-slate-400 disabled:no-underline">
                  {mfaResending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
                  {(mfaResendCooldown > 0 && !mfaState.mfa_send_failed)
                    ? `Reenviar código en ${mfaResendCooldown}s`
                    : "Reenviar código"}
                </button>
              </div>

              <p data-testid="mfa-aviso-codigos-vigentes" className="text-[11px] text-slate-500">
                Si pides otro código, el anterior sigue funcionando: puedes usar cualquiera de
                los últimos 3 que te lleguen.
              </p>

              {/* Punto 13 — puerta de emergencia. Solo aparece cuando el backend confirma que
                  el usuario tiene códigos de respaldo vigentes (hoy: super_admin). */}
              {mfaState.backup_disponible && (
                <div className="border-t border-slate-100 pt-3">
                  <button type="button" data-testid="mfa-usar-backup"
                    onClick={() => { setModoBackup(true); setMfaError(""); }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:underline">
                    <KeyRound className="h-3.5 w-3.5" /> Usar código de respaldo
                  </button>
                  <p className="mt-1 text-[11px] text-slate-500">
                    ¿No llega el correo? Entra con uno de tus {mfaState.backup_restantes} códigos de respaldo.
                  </p>
                </div>
              )}
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">Correo electrónico</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.mx" autoComplete="username" />
              {dosfaEntidad && (
                <div data-testid="dosfa-off-banner"
                  className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <ShieldOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span>
                    El segundo factor (2FA) está <b>desactivado</b> para esta entidad
                    {dosfaEntidad.entidad ? <> (<b className="font-mono">{dosfaEntidad.entidad}</b>)</> : ""} por
                    decisión del Super Administrador. Ingresarás solo con correo y contraseña.
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">Contraseña</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
              <div className="mt-1.5 text-right">
                <Link to="/recuperar" data-testid="forgot-password-link" className="text-xs font-medium text-accent hover:underline">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {emailNoVerificado && (
              <div data-testid="login-email-no-verificado" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                <div className="mb-2 flex items-start gap-2">
                  <MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-semibold">Confirma tu correo para acceder</p>
                    <p className="mt-0.5 text-xs text-amber-800">
                      Enviamos un enlace de verificación a <span className="font-mono">{emailNoVerificado.email}</span>.
                      Revisa tu bandeja de entrada (y spam). El enlace expira en 1 hora.
                    </p>
                  </div>
                </div>
                {reenviado ? (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="login-reenviado-ok">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {reenvioResultado?.mensaje || "Enlace reenviado."}
                  </div>
                ) : (
                  <>
                    {reenvioResultado && reenvioResultado.codigo !== "enviado" && (
                      <div data-testid="login-reenvio-resultado"
                           className={`mb-2 flex items-start gap-1.5 rounded-md px-2.5 py-2 text-xs ${
                             reenvioResultado.codigo === "fallo_envio" || reenvioResultado.codigo === "error"
                               ? "border border-red-200 bg-red-50 text-red-800"
                               : "bg-white/70 text-amber-900"}`}>
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {reenvioResultado.mensaje}
                      </div>
                    )}
                    <Button data-testid="login-reenviar-btn" type="button" size="sm" variant="outline"
                      disabled={reenviando} onClick={reenviarVerificacion}
                      className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100">
                      {reenviando ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />}
                      Reenviar correo de verificación
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Cloudflare Turnstile — captcha "no soy un robot" en español (jul-2026).
                El widget renderiza en modo Managed (invisible cuando confía, challenge visible
                si detecta riesgo). Se resetea automáticamente tras cada submit y expira solo
                cuando Cloudflare lo indica (~5 min). */}
            <div className="flex justify-center" data-testid="login-turnstile-wrapper">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ language: "es", theme: "light" }}
                onSuccess={(token) => setCfToken(token)}
                onExpire={() => setCfToken("")}
                onError={() => setCfToken("")}
              />
            </div>

            {DEMO_ACTIVO && (
              <div data-testid="login-aviso-demo"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <b>Entorno de demostración.</b> Las credenciales ya están precargadas: solo pulsa
                <b> Entrar</b>. Toda la información es ficticia.
              </div>
            )}

            <Button data-testid="login-submit" type="submit" disabled={loading || !cfToken}
              className="w-full bg-primary hover:bg-sidebar-hover">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Entrar <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          )}

          {!mfaState && (
          <p className="mt-8 text-center text-sm text-slate-500">
            ¿Tu entidad aún no está registrada?{" "}
            <Link to="/registro" data-testid="go-register" className="font-medium text-accent hover:underline">
              Registrar entidad
            </Link>
          </p>
          )}
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-1/2">
        <img src={HERO} alt="Edificio corporativo" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/95" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <VoraLogo variant="stacked" tone="light" className="mt-4" />
          <div>
            <h2 className="font-heading text-3xl font-semibold leading-tight">
              Cumplimiento automatizado para entidades financieras y actividades vulnerables.
            </h2>
            <p className="mt-4 max-w-md text-sm text-slate-200">
              SOFOM ENR, PLD/CNBV, CONDUSEF, expedientes, cartera y calendario regulatorio.
              Arquitectura multi-régimen preparada para actividades vulnerables y más.
            </p>
            <div className="mt-8 flex gap-6 text-sm">
              <div><p className="font-heading text-2xl font-semibold">Multiempresa</p><p className="text-sage">Información aislada por entidad</p></div>
              <div><p className="font-heading text-2xl font-semibold">Multi-régimen</p><p className="text-sage">Motor normativo configurable</p></div>
            </div>
            <p className="mt-10 text-xs text-slate-400">
              Sistema configurable para apoyar el cumplimiento. No constituye certificación de autoridad alguna.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
